<script lang="ts" setup>
import {cx} from 'class-variance-authority'
import {lexicalToPlaintext} from '~/utils/lexical'
import {resolveRelations, resolveMediaUrl} from '~/utils/relations'
import PainPoints from '~/components/warehouse/pain-points.vue'
import Diagram from '~/components/warehouse/diagram.vue'
import BlogSection from '~/components/workshop/blog-section.vue'

import TargetCard from '~/components/warehouse/target-card.vue'
import Testimonial from '~/components/warehouse/testimonial.vue'

const {fetch} = useWarehouse()
const {data: initialData} = await useAsyncData('warehouse', () => fetch())
const {data: warehouse} = useLivePreview({initialData: initialData.value, depth: 4})

if (!warehouse.value) {
  throw createError({status: 503, message: 'Could not load the page.'})
}

const lp = useLocalePath()

const {
  public: {baseUrl},
} = useRuntimeConfig()

useSchemaOrg([
  {
    '@type': 'FAQPage',
    mainEntity: computed(
      () =>
        resolveRelations(warehouse.value?.faq?.questions).map(item => ({
          '@type': 'Question',
          name: item.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: lexicalToPlaintext(item.body),
          },
        })) || []
    ),
  },
])

const seo = warehouse.value?.meta

useSeoMeta({
  title: seo?.title,
  description: seo?.description,
  ogUrl: `${baseUrl}/logistics/custom-warehouse-management-system`,
  ogTitle: seo?.ogTitle || seo?.title,
  ogDescription: seo?.ogDescription || seo?.description,
  ogImage: resolveMediaUrl(seo?.image),
  ogImageAlt: resolveMediaAlt(seo?.image),
})
</script>

<template>
  <main v-if="warehouse" class="flex w-full flex-col overflow-hidden">
    <Container
      v-if="warehouse.hero"
      class="screen-content flex flex-col items-center space-y-6 px-0 pb-16 pt-32 lg:px-[15rem] lg:pb-16 lg:pt-32"
    >
      <Heading as="h1" size="caption">{{ warehouse.hero.eyebrow }}</Heading>

      <Heading size="display" as="h2" class="w-full text-center xs:mb-8 lg:mb-4">
        <span :class="cx('pr-2')">{{ $t('warehouse.title') }}</span
        ><br />
        <span>{{ $t('warehouse.sub_title') }}</span>
      </Heading>

      <Paragraph as="p" class="text-sm text-gray-600 xs:px-12 xs:text-center lg:w-3/4 lg:px-0" size="medium">
        {{ warehouse.hero.body }}
      </Paragraph>

      <div class="h-[4rem]"></div>
      <Action size="lg" intent="main" to="/contact" as="link">
        {{ $t('warehouse.cta', 'Get a free consultation') }}
      </Action>
    </Container>

    <Container class="border-t">
      <div class="mx-auto max-w-5xl px-6 sm:px-12">
        <PainPoints class="py-16 sm:py-24 lg:py-32" :data="warehouse.painPoints" />
      </div>
    </Container>

    <Container v-if="warehouse.customBusiness" class="border-t">
      <div class="mx-auto max-w-5xl px-6 sm:px-12">
        <div class="flex flex-col items-center gap-8 py-16 sm:py-20 lg:flex-row lg:gap-12 lg:py-24">
          <div class="w-full lg:w-1/2">
            <Heading size="medium" as="h3">
              {{ $t('warehouse.custom_business.title') }}
            </Heading>

            <Heading class="mb-6 mt-1 text-main-600" as="h3">
              {{ $t('warehouse.custom_business.sub_title') }}
            </Heading>

            <RichText class="w-full lg:w-11/12" :raw="warehouse.customBusiness.body" />
          </div>

          <div class="w-full lg:w-3/4">
            <NuxtImg
              class="h-auto w-full rounded-lg"
              :src="resolveMediaUrl(warehouse.customBusiness.image)"
              :alt="$t('warehouse.custom_business.title')"
            />
          </div>
        </div>
      </div>
    </Container>

    <Container v-if="warehouse.theTeam" class="border-t">
      <div class="mx-auto max-w-5xl px-12 md:px-6">
        <div class="flex-col justify-between py-24 md:flex">
          <div>
            <Heading as="h3" size="caption">{{ warehouse.theTeam.eyebrow }}</Heading>
            <Heading as="h3" size="medium">{{ warehouse.theTeam.title }}</Heading>
          </div>

          <Paragraph class="max-w-sm" as="p">{{ warehouse.theTeam.body }}</Paragraph>
        </div>

        <div class="mb-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          <div v-for="stat in warehouse.theTeam.stats" :key="stat.title" class="flex flex-col">
            <Heading size="small">{{ stat.title }}</Heading>
            <Paragraph as="p">{{ stat.data }}</Paragraph>
          </div>
        </div>

        <NuxtImg class="h-full w-full" :src="resolveMediaUrl(warehouse.theTeam.map)" />
        <NuxtImg class="mx-auto w-[800px] py-12" :src="resolveMediaUrl(warehouse.theTeam.award)" />
      </div>
    </Container>

    <div class="border-t">
      <Container v-if="warehouse.features" class="mx-auto max-w-4xl px-12 py-24 lg:px-0">
        <Heading as="h2" size="caption">{{ warehouse.features.eyebrow }}</Heading>
        <Heading as="h3" size="medium">{{ warehouse.features.title }}</Heading>

        <Paragraph class="mt-4 w-full lg:w-1/3" as="p">{{ warehouse.features.body }}</Paragraph>

        <div v-if="warehouse.features.features" class="mt-10 flex justify-between border-t pt-6">
          <ul class="space-y-4">
            <li
              v-for="(feature, index) in warehouse.features.features.slice(0, 4)"
              :key="index"
              class="flex items-center"
            >
              <Icon
                class="h-6 w-6 flex-shrink-0 rounded-full bg-green-200 p-1 text-green-800"
                name="material-symbols:check-rounded"
              />
              <span class="ml-3">{{ feature.title }}</span>
            </li>
          </ul>

          <ul class="space-y-4">
            <li
              v-for="(feature, index) in warehouse.features.features.slice(4, 7)"
              :key="index"
              class="flex items-center"
            >
              <Icon
                class="h-6 w-6 flex-shrink-0 rounded-full bg-green-200 p-1 text-green-800"
                name="material-symbols:check-rounded"
              />
              <span class="ml-3">{{ feature.title }}</span>
            </li>
          </ul>
        </div>
      </Container>

      <div v-if="warehouse.benefits" class="border-t">
        <Container class="mx-auto max-w-4xl px-12 py-24 lg:px-0">
          <Heading as="h2" size="caption">{{ warehouse.benefits.eyebrow }}</Heading>
          <Heading as="h3" size="medium">{{ warehouse.benefits.title }}</Heading>

          <Paragraph class="mt-4 w-full lg:w-1/3" as="p">{{ warehouse.benefits.body }}</Paragraph>

          <div class="mt-10">
            <div class="flex justify-between gap-2">
              <ul class="w-1/2 space-y-4">
                <div class="flex items-center justify-between">
                  <Heading as="h4" size="medium" class="text-lg !font-medium">Before</Heading>
                  <Icon name="material-symbols:arrow-forward" />
                </div>

                <div class="mt-4 h-[1px] w-full bg-gradient-to-r from-main-400 via-green-200 to-green-300"></div>
                <li v-for="(before, index) in warehouse.benefits.before" :key="index" class="flex items-center">
                  <Icon
                    class="h-6 w-6 flex-shrink-0 rounded-full bg-red-200 p-1 text-red-800"
                    name="ic:baseline-minus"
                  />
                  <span class="ml-3">{{ before.title }}</span>
                </li>
              </ul>

              <ul class="flex-1 space-y-4">
                <Heading as="h4" size="medium" class="text-lg !font-medium">After</Heading>
                <div class="mt-4 h-[1px] w-full bg-gradient-to-r from-main-400 via-green-200 to-green-300"></div>

                <li v-for="(after, index) in warehouse.benefits.after" :key="index" class="flex items-center">
                  <Icon
                    class="h-6 w-6 flex-shrink-0 rounded-full bg-green-200 p-1 text-green-800"
                    name="material-symbols:check-rounded"
                  />
                  <span class="ml-3">{{ after.title }}</span>
                </li>
              </ul>
            </div>
          </div>
        </Container>
      </div>
    </div>

    <Diagram :data="warehouse.diagram" />

    <div class="border-t">
      <Container class="mx-auto max-w-5xl px-12">
        <div class="mx-auto flex max-w-2xl flex-col items-center justify-center space-y-4 pt-4 text-center">
          <Heading as="h2" size="caption">{{ $t('warehouse.target.eyebrow') }}</Heading>
          <Heading as="h3" size="medium">{{ $t('warehouse.target.title') }}</Heading>

          <Paragraph as="p">{{ $t('warehouse.target.body') }}</Paragraph>
        </div>

        <!-- Note: target section uses i18n content, not CMS data -->
        <div class="mx-auto mt-16 grid max-w-xl grid-cols-1 justify-center md:grid-cols-2">
          <!-- Target cards would come from a block if implemented in CMS -->
        </div>
      </Container>
    </div>

    <Testimonial
      v-if="warehouse.singleTestimonial"
      :large="true"
      class="border-b-0 py-14 lg:py-24"
      :data="warehouse.singleTestimonial"
    />

    <Container v-if="warehouse.faq" class="screen-content flex flex-col items-center py-16 md:pb-16 lg:pb-10 lg:pt-16">
      <Heading size="caption" as="h2" class="text-center">{{ warehouse.faq.headline }}</Heading>
      <Heading as="h3" size="large" class="w-full text-center lg:w-1/2">{{ warehouse.faq.title }}</Heading>
      <Paragraph class="mt-2 w-full pb-6 text-left lg:w-1/2">{{ warehouse.faq.body }}</Paragraph>

      <Accordion
        v-if="warehouse.faq.questions"
        class="mt-8 pb-16 text-sm xs:px-12 xl:px-52"
        :items="warehouse.faq.questions"
      />
    </Container>

    <BlogSection v-if="warehouse.blog" :data="warehouse.blog" />

    <LazyCta class="pt-24" :href="lp('/contact')" :label="$t('home.contact.ctaLabel')" />
  </main>
</template>
